import { useState, type FormEvent } from 'react'
import { Alert, Box, Button, Chip, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import EditOutlined from '@mui/icons-material/EditOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  cboDigits,
  cboReviewKey,
  fetchCboSuggestions,
  formatCbo,
  JOBS_QUERY_KEY,
  suggestCbo,
  updateJobCbo,
  type CboSuggestion,
  type CboSuggestions,
} from '../api/jobs'
import { jobRecordKey } from '../api/jobDescription'
import { t } from '../i18n'

/**
 * s69 / 2m (Marcelo 28/10/2026 14:30, "C2 sim"): o CBO do cargo e DIGITADO na ficha -- antes so era mostrado e so
 * entrava por planilha. Quem tem job_architecture.job.write ve o lapis; os demais so leem. Aceita 0000-00 ou 6 digitos;
 * vazio limpa. Grava pelo PATCH /api/jobs/{id}/basics (o servidor confere o formato de novo, CBOCode).
 * s69 / 2o (V1): "Sugerir com a VIGA" lista candidatos da TABELA OFICIAL (sem IA); clicar preenche o campo, e a pessoa
 * confere e salva -- nada e gravado sem o "Salvar".
 * s69 / 2p (ADR-0035 D4/D6): o botao pede a VIGA (a IA so ESCOLHE entre candidatos oficiais e diz o motivo); sem IA,
 * vem a etapa 1 (nome + atividades). Selo "CBO a revisar" ao lado do CBO gravado quando ele nao existe na tabela ou
 * nao esta entre as ocupacoes oficiais mais proximas -- so aviso, nada e trocado.
 */
/** s69 / 2q (Marcelo 28/10 19:15, "analista de PCP aparece analista musical"): sem IA, a lista e so a busca na
 * tabela (etapa 1) -- o topo dela e ruido alem dos 3 primeiros (medido na LUTEPEL: entre 3 61%, entre 5 63%). */
const MAX_SEM_IA = 3

function visiveis(r: CboSuggestions): CboSuggestion[] {
  return r.mode === 'viga' ? r.items : r.items.slice(0, MAX_SEM_IA)
}

function rotuloVia(s: CboSuggestion): string {
  if (s.reason) return ''
  if (s.via === 'sinonimo' && s.matched) return ` (${t.jobRecord.cboVia(s.matched)})`
  if (s.via === 'atividades') return ` (${t.jobRecord.cboViaAtividades})`
  return ''
}

export function CboField({ jobId, cbo, pode }: { jobId: string; cbo: string | null; pode: boolean }) {
  const qc = useQueryClient()
  const [editando, setEditando] = useState(false)
  const [texto, setTexto] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  // s69 / 2o (V1): candidatos da tabela oficial (null = ainda nao pedidos)
  const [sugestoes, setSugestoes] = useState<CboSuggestions | null>(null)
  // s69 / 2p (D6): selo do CBO gravado (sem custo: etapa 1, sem IA); erro = sem selo
  const selo = useQuery({
    queryKey: cboReviewKey(jobId, cbo),
    queryFn: () => fetchCboSuggestions(jobId),
    enabled: !!cbo && !editando,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
  const busca = useMutation({
    mutationFn: () => suggestCbo(jobId),
    onSuccess: (r) => setSugestoes(r),
    onError: () => setErro(t.jobRecord.cboSuggestError),
  })
  const m = useMutation({
    mutationFn: (d: string) => updateJobCbo(jobId, d),
    onSuccess: () => {
      setEditando(false)
      void qc.invalidateQueries({ queryKey: jobRecordKey(jobId) })
      void qc.invalidateQueries({ queryKey: JOBS_QUERY_KEY })
    },
    onError: () => setErro(t.jobRecord.cboSaveError),
  })
  const valor = formatCbo(cbo)

  if (editando)
    return (
      <Box
        component="form"
        data-testid="cbo-form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault()
          const d = cboDigits(texto)
          if (d.length !== 0 && d.length !== 6) {
            setErro(t.jobRecord.cboInvalid)
            return
          }
          setErro(null)
          m.mutate(d)
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="0000-00"
            slotProps={{ htmlInput: { maxLength: 7, inputMode: 'numeric', 'aria-label': t.jobRecord.fieldCbo } }}
            sx={{ width: 110 }}
          />
          <Button type="submit" size="small" variant="contained" disabled={m.isPending}>
            {t.jobRecord.cboSave}
          </Button>
          <Button size="small" onClick={() => { setEditando(false); setErro(null); setSugestoes(null) }}>
            {t.jobRecord.cboCancel}
          </Button>
          <Button size="small" variant="text" data-testid="cbo-sugerir" disabled={busca.isPending} onClick={() => busca.mutate()}>
            {t.jobRecord.cboSuggest}
          </Button>
        </Stack>
        {sugestoes && (
          <Box data-testid="cbo-sugestoes" sx={{ mt: 0.75 }}>
            {!sugestoes.available ? (
              <Typography variant="caption" color="text.secondary">{t.jobRecord.cboSuggestUnavailable}</Typography>
            ) : sugestoes.items.length === 0 ? (
              <Typography variant="caption" color="text.secondary">{t.jobRecord.cboSuggestNone}</Typography>
            ) : (
              <>
                {sugestoes.degraded && (
                  <Typography variant="caption" color="warning.main" component="p" data-testid="cbo-degradado">
                    {t.jobRecord.cboSuggestDegraded}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" component="p" data-testid="cbo-modo">
                  {sugestoes.mode === 'viga' ? t.jobRecord.cboSuggestHintViga : t.jobRecord.cboSuggestHint}
                </Typography>
                <Stack spacing={0.5} alignItems="flex-start" sx={{ mt: 0.5 }}>
                  {visiveis(sugestoes).map((s) => (
                    <Box key={s.code}>
                      <Chip
                        size="small"
                        variant="outlined"
                        data-testid={`cbo-sugestao-${s.code}`}
                        onClick={() => setTexto(formatCbo(s.code) ?? s.code)}
                        label={`${formatCbo(s.code)} · ${s.title}${rotuloVia(s)}`}
                        sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
                      />
                      {s.reason && (
                        <Typography variant="caption" color="text.secondary" component="p" data-testid={`cbo-motivo-${s.code}`} sx={{ ml: 1 }}>
                          {s.reason}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" component="p">
          {t.jobRecord.cboHint}
        </Typography>
        {erro && (
          <Alert severity="warning" data-testid="cbo-erro" sx={{ mt: 0.5 }}>
            {erro}
          </Alert>
        )}
      </Box>
    )

  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
      <Typography
        variant="body2"
        component="span"
        data-testid="cbo-valor"
        color={valor ? 'text.primary' : 'text.secondary'}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {valor ?? t.jobRecord.notInformed}
      </Typography>
      {selo.data?.review?.to_review && (
        <Tooltip title={selo.data.review.exists ? t.jobRecord.cboReviewHint : t.jobRecord.cboReviewMissing}>
          <Chip size="small" color="warning" variant="outlined" data-testid="cbo-a-revisar" label={t.jobRecord.cboReview} />
        </Tooltip>
      )}
      {pode && (
        <Tooltip title={t.jobRecord.cboEdit}>
          <IconButton
            size="small"
            aria-label={t.jobRecord.cboEdit}
            onClick={() => {
              setTexto(valor ?? '')
              setErro(null)
              setSugestoes(null)
              setEditando(true)
            }}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  )
}
